/**
 * Collect — our collection points (order to invoice doc 104 §7, §9.1) and who is collecting: the staff Delivery
 * section's Collect tab and each delivery card read the same fields. The points are
 * read by the caller (the item page reads them once; a checkout's delivery preview carries them), so this shows them as they
 * are loading, failed (with a way to try again), none set up, or ready to choose from.
 */
import { useId } from "react";
import { Loader2 } from "lucide-react";
import { collectionPointText } from "@shared-core/domain/checkout-delivery";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";

export interface CollectionPointOption {
  id: string;
  name: string;
  postcode: string | null;
}

export type CollectionPointsState =
  | { status: "idle" | "loading" }
  | { status: "ready"; points: readonly CollectionPointOption[] }
  | { status: "error" };

export function CollectionFields({
  state,
  onRetry,
  pointId,
  onPointChange,
  pointError,
  collectorName,
  onCollectorNameChange,
  collectorNameError = null,
  collectorPhone,
  onCollectorPhoneChange,
  collectorPhoneError = null,
  onCollectorBlur,
  disabled,
}: {
  state: CollectionPointsState;
  /** Reads the points again after a failed read; absent where the points arrive with the page. */
  onRetry?: () => void;
  pointId: string | null;
  onPointChange: (id: string) => void;
  /** The server's refusal of the point (or of there being none set up), beside the points. */
  pointError: string | null;
  collectorName: string;
  onCollectorNameChange: (value: string) => void;
  collectorNameError?: string | null;
  collectorPhone: string;
  onCollectorPhoneChange: (value: string) => void;
  collectorPhoneError?: string | null;
  /** Focus left the name or the phone: the place to read the delivery again. */
  onCollectorBlur?: () => void;
  disabled: boolean;
}) {
  const id = useId();
  if (state.status === "error")
    return (
      <div role="alert" className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-red-700">
          The collection points could not be loaded.
        </span>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            disabled={disabled}
            className="text-xs font-medium text-primary hover:underline disabled:opacity-50"
          >
            Try again
          </button>
        )}
      </div>
    );
  if (state.status !== "ready")
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        Loading the collection points…
      </p>
    );
  if (state.points.length === 0)
    return (
      <p className="text-sm text-muted-foreground">
        Collection isn&rsquo;t set up yet.
      </p>
    );
  const errorId = `${id}-point-error`;
  return (
    <div className="space-y-3">
      <fieldset className="space-y-1">
        <legend className="text-sm font-medium">Collection from</legend>
        {state.points.map((p) => (
          <label
            key={p.id}
            className="flex items-center gap-2 text-sm cursor-pointer"
          >
            <input
              type="radio"
              name={`${id}-collection-point`}
              checked={pointId === p.id}
              onChange={() => onPointChange(p.id)}
              disabled={disabled}
              aria-invalid={pointError ? true : undefined}
              aria-describedby={pointError ? errorId : undefined}
            />
            {collectionPointText(p)}
          </label>
        ))}
        {pointError && (
          <p id={errorId} className="text-sm text-red-600">
            {pointError}
          </p>
        )}
      </fieldset>
      <fieldset className="space-y-1">
        <legend className="text-sm font-medium">Who&rsquo;s collecting</legend>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor={`${id}-name`}>Name</Label>
            <Input
              id={`${id}-name`}
              value={collectorName}
              onChange={(e) => onCollectorNameChange(e.target.value)}
              onBlur={onCollectorBlur}
              disabled={disabled}
              autoComplete="off"
              maxLength={100}
              aria-invalid={collectorNameError ? true : undefined}
              aria-describedby={
                collectorNameError ? `${id}-name-error` : undefined
              }
            />
            {collectorNameError && (
              <p id={`${id}-name-error`} className="text-sm text-red-600">
                {collectorNameError}
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor={`${id}-phone`}>Phone</Label>
            <Input
              id={`${id}-phone`}
              type="tel"
              value={collectorPhone}
              onChange={(e) => onCollectorPhoneChange(e.target.value)}
              onBlur={onCollectorBlur}
              disabled={disabled}
              autoComplete="off"
              maxLength={40}
              aria-invalid={collectorPhoneError ? true : undefined}
              aria-describedby={
                collectorPhoneError ? `${id}-phone-error` : undefined
              }
            />
            {collectorPhoneError && (
              <p id={`${id}-phone-error`} className="text-sm text-red-600">
                {collectorPhoneError}
              </p>
            )}
          </div>
        </div>
      </fieldset>
    </div>
  );
}
